/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
declare global {
  namespace API {
    type AddressType = "primary" | "mailing";

    type Address = {
      street_address: string;
      street_address_2: string;
      city: string;
      region_code: string;
      postal_code: string;
      country_code: string;
      type?: AddressType;
    };

    type NoAddress = {
      street_address: "";
      street_address_2: "";
      city: "";
      region_code: "";
      postal_code: "";
      country_code: "";
      type: "";
    };

    type MaybeAddress = Address | NoAddress;
  }
}

type DeserializedAddress = API.Address;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Address extends DeserializedAddress {}

class Address {
  static readonly PRIMARY = "primary";

  static readonly MAILING = "mailing";

  static readonly COUNTRIES = {
    US: "US",
  };

  constructor(props: Partial<API.MaybeAddress> = {}) {
    Object.assign(this, {
      street_address: "",
      street_address_2: "",
      city: "",
      region_code: "",
      postal_code: "",
      country_code: Address.COUNTRIES.US,
      ...props,
    });
  }

  toString() {
    let fullStreetAddress = "";
    if (this.street_address) {
      fullStreetAddress = this.street_address_2
        ? `${this.street_address} ${this.street_address_2},`
        : `${this.street_address},`;
    }
    // eslint-disable-next-line no-return-assign
    return [
      fullStreetAddress,
      this.city ? `${this.city},` : "",
      this.region_code,
      this.postal_code,
      // ToDo: figure out what this is trying to do and do it some other way
      // when country_code != street_address, lets 'Same as
      // physical/residential address' display for Mailing Address, rather than US.
      (this.country_code = this.street_address ? this.country_code : ""),
    ]
      .filter((a) => a)
      .join(" ");
  }

  toJSON() {
    return {
      street_address: this.street_address,
      street_address_2: this.street_address_2,
      city: this.city,
      region_code: this.region_code,
      postal_code: this.postal_code,
      country_code: this.country_code,
      type: this.type,
    };
  }

  validate(): asserts this is API.Address {
    const requiredAddressFields = [
      "street_address",
      "city",
      "region_code",
      "postal_code",
      "country_code",
    ] as const;
    requiredAddressFields.forEach((field) => {
      if (!this[field]) throw new Error(`Address field ${field} is required`);
    });
  }

  isPrimary() {
    return this.type === Address.PRIMARY;
  }

  isMailing() {
    return this.type === Address.MAILING;
  }
}

export function updateAddress(
  previous?: Address | null,
  update?: Address,
): Address;
export function updateAddress(
  previous: any,
  update: Address | null,
): Address | null;
export function updateAddress(
  previous: Address | null = new Address(),
  update: Address | null = new Address(),
) {
  if (update === null) return null;
  return new Address({
    ...(previous || new Address()),
    ...update,
  });
}

export default Address;

/**
 * Country-specific address field labels for international addresses.
 *
 * When a country is not listed, default labels are used:
 *   region: "Region", city: "City", postalCode: "Postal code"
 */

type AddressLabels = {
  region?: string;
  city?: string;
  postalCode?: string;
};

/**
 * Overrides keyed by ISO 3166-1 alpha-2 country code.
 * Only fields that differ from the defaults need to be specified.
 */
const COUNTRY_ADDRESS_LABELS: Record<string, AddressLabels> = {
  AU: { region: "State/Territory", postalCode: "Postcode" },
  BR: { region: "State" },
  CA: { region: "Province", postalCode: "Postal code" },
  CN: { region: "Province", city: "City/Prefecture" },
  DE: { region: "State", postalCode: "Postcode" },
  FR: { region: "Region", postalCode: "Postal code" },
  GB: { region: "County", postalCode: "Postcode" },
  HK: { region: "District" },
  IN: { region: "State", postalCode: "PIN code" },
  JP: { region: "Prefecture", postalCode: "Postal code" },
  KR: { region: "Province", postalCode: "Postal code" },
  MX: { region: "State", postalCode: "Postal code" },
  NG: { region: "State" },
  PH: { region: "Province", postalCode: "ZIP code" },
  US: { region: "State" },
  TW: { region: "City", city: "District" },
  ZA: { region: "Province", postalCode: "Postal code" },
};

const DEFAULT_ADDRESS_LABELS: Required<AddressLabels> = {
  region: "Region",
  city: "City",
  postalCode: "Postal code",
};

export const getAddressLabels = (
  countryCode: string,
): Required<AddressLabels> => ({
  ...DEFAULT_ADDRESS_LABELS,
  ...COUNTRY_ADDRESS_LABELS[countryCode.toUpperCase()],
});
